// General topic-level Socratic AI Tutor chat — Home page's browse-by-topic
// flow. Unlike AITutorPanel.tsx (Pink-bucket, per-question, gated server-side
// to a struggling card), this posts to POST /tutor/topic with a subtopicId —
// no bucket to gate on here, just auth + rate limiting (see routes/tutor.ts).
// Stateless: nothing is persisted server-side, a page refresh clears it.
import { useState } from 'react';
import { apiErrorMessage, apiPost } from '@/lib/api';

interface TutorTurn {
  role: 'student' | 'tutor';
  message: string;
}

interface TutorReplyResult {
  tutorMessage: string;
  suggestScaffold: boolean;
}

const MAX_HISTORY = 8;

export function AITutorTopicPanel({ subtopicId, topicLabel }: { subtopicId: string; topicLabel: string }) {
  const [history, setHistory] = useState<TutorTurn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    const nextHistory: TutorTurn[] = [...history, { role: 'student', message: text } as TutorTurn].slice(-MAX_HISTORY);
    setHistory(nextHistory);
    setInput('');
    setSending(true);
    try {
      const result = await apiPost<TutorReplyResult>('/tutor/topic', { subtopicId, studentHistory: nextHistory });
      setHistory((h) => [...h, { role: 'tutor' as const, message: result.tutorMessage }].slice(-MAX_HISTORY));
    } catch (err: unknown) {
      setError(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-line bg-paper">
      <div className="border-b border-line px-3 py-2">
        <p className="text-sm font-semibold text-ink">AI Tutor</p>
        <p className="mt-0.5 text-xs text-ink-faint">Ask about {topicLabel}</p>
      </div>

      <div className="max-h-56 space-y-2 overflow-y-auto p-3">
        {history.length === 0 && (
          <p className="text-sm text-ink-faint">Not sure where to start with this topic? Ask away.</p>
        )}
        {history.map((turn, i) => (
          <div key={i} className={turn.role === 'student' ? 'text-right' : 'text-left'}>
            <span
              className={[
                'inline-block max-w-[85%] rounded-2xl px-3 py-2 text-sm',
                turn.role === 'student' ? 'bg-slate-900 text-white' : 'bg-paper-sunk text-ink',
              ].join(' ')}
            >
              {turn.message}
            </span>
          </div>
        ))}
        {sending && <p className="text-xs text-ink-faint">Tutor is thinking…</p>}
      </div>

      {error && <p className="px-3 py-1 text-xs text-danger">{error}</p>}

      <div className="flex gap-2 border-t border-line p-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask the tutor…"
          maxLength={1_000}
          className="flex-1 rounded-lg border border-line bg-card px-3 py-2 text-sm outline-none focus:border-forest"
          disabled={sending}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !input.trim()}
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </div>
  );
}
